// Settings (prototype scene 21). Shows whether each AI provider is connected — never the key itself; keys live
// in the server's .env only. Tally status checks are not shown yet.
import { useEffect, useState } from 'react';
import { getSettings, patchSettings, errorText } from '../api';
import { useLoad, usd, modelShort } from '../fmt';
import { canListen, canSpeak, hasBanglaVoice, LISTEN_LANGS } from '../useVoice';

const Switch = ({ on, disabled, onChange, label }) => (
  <label className="switch"><input type="checkbox" checked={!!on} disabled={disabled} onChange={(e) => onChange && onChange(e.target.checked)} aria-label={label} /><span /></label>
);

export default function Settings({ speak, onSpeak, theme, onTheme, officeLife, onOfficeLife, listenLang, onListenLang }) {
  const { data: s, error, reload } = useLoad(getSettings);
  const [limitDraft, setLimitDraft] = useState('');
  const [limitMsg, setLimitMsg] = useState(null);
  // The field shows the current limit as a normal value, and again after each save.
  const savedLimit = s ? s.dailyLimitUSD : null;
  useEffect(() => { if (savedLimit != null) setLimitDraft(String(savedLimit)); }, [savedLimit]);
  const saveLimit = async () => {
    setLimitMsg(null);
    try { await patchSettings({ dailyLimitUSD: Number(limitDraft) }); setLimitMsg('Saved.'); reload(); }
    catch (err) { setLimitMsg(errorText(err)); }
  };
  if (error) return <div className="err">{errorText(error)}</div>;
  const loadingNote = <p style={{ margin: 0, color: 'var(--muted)', fontSize: 13 }}>Loading…</p>;
  const used = s ? s.usage.todayUSD : 0, limit = s ? s.dailyLimitUSD : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="vh"><div><h2>Settings</h2><p>Only the boss can see this page.</p></div></div>
      <div className="set">
        <div className="panel">
          <h3>AI providers</h3>
          <p>Keys are kept on the server. This page only shows whether each one is connected.</p>
          {!s && loadingNote}
          {(s ? s.providers : []).map((p) => (
            <div className="prov" key={p.id}>
              <b>{p.name}</b>
              <span className={p.connected ? 'pill idle' : p.optional ? 'pill' : 'pill error'}><i />{p.connected ? (p.inUse ? 'Connected · in use' : 'Connected') : p.optional ? 'Not set up (optional)' : 'Not connected'}</span>
            </div>
          ))}
        </div>
        <div className="panel">
          <h3>Company tools</h3>
          <p>The link to orders, PIs, accounts and Tally. Employees can never do more than your own login.</p>
          <div className="prov">
            <b>Company system (MCP)</b>
            {s && <span className={`pill ${s.companyTools.available ? 'idle' : 'error'}`}><i />{s.companyTools.available ? `Connected · ${s.companyTools.available} tools` : 'No access'}</span>}
          </div>
          <div className="prov"><b>Your access</b>{s ? <span className="pill">{s.companyTools.accessLevel === 'write' ? 'Look up and change (with approval)' : s.companyTools.accessLevel === 'read' ? 'Look up only' : 'None'}</span> : <span style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</span>}</div>
        </div>
        <div className="panel">
          <h3>Voice, language and office</h3>
          <p>How the boss talks to the office, and how lively it is.</p>
          <div className="trow">
            <span>Boss speaks<small>What the mic listens for. Bangla also catches the usual English words. Also on the chat's বাং / EN button.</small></span>
            <select aria-label="Boss speaks" value={listenLang} onChange={(e) => onListenLang(e.target.value)}>
              {Object.entries(LISTEN_LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="trow"><span>Read every reply aloud<small>Answers to a question you asked by voice are always read aloud.</small></span><Switch on={speak} onChange={onSpeak} label="Read replies aloud" /></div>
          <div className="trow">
            <span>This browser<small>The mic works in Chrome and Edge; they turn speech into text online (Google / Microsoft).</small></span>
            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <span className={`pill ${canListen ? 'idle' : 'error'}`}><i />{canListen ? 'Mic ready' : 'No mic here'}</span>
              <span className={`pill ${canSpeak && hasBanglaVoice() ? 'idle' : ''}`}><i />{!canSpeak ? 'No voice' : hasBanglaVoice() ? 'Bangla voice' : 'English voice only'}</span>
            </span>
          </div>
          <div className="trow">
            <span>Office life<small>Look away and free employees sneak off: tea, gossip, jokes, a dance. Move the mouse to catch them! Never while they work.</small></span>
            <Switch on={officeLife} onChange={onOfficeLife} label="Office life" />
          </div>
          {s && (
            <div className="trow">
              <span>Spicy humour<small>Real Bangladesh politics jokes and rough Dhaka language in the office small talk. Turn off if others watch the screen.</small></span>
              <Switch on={s.lifeHumour === 'spicy'} label="Spicy humour"
                onChange={async (v) => { try { await patchSettings({ lifeHumour: v ? 'spicy' : 'clean' }); reload(); } catch (err) { setLimitMsg(errorText(err)); } }} />
            </div>
          )}
          {s && <div className="trow"><span>Fresh small talk each day<small>Written by the cheap AI model from today's Dhaka news and trends: once in the morning, once after 2 pm. Under 1 US cent a day.</small></span><span className={`pill ${s.officeLifeAI ? 'idle' : ''}`}><i />{s.officeLifeAI ? 'On' : 'Off'}</span></div>}
          <div className="trow">
            <span>Colours</span>
            <select aria-label="Colours" value={theme} onChange={(e) => onTheme(e.target.value)}>
              <option value="auto">Same as this computer</option><option value="light">Light</option><option value="dark">Dark</option>
            </select>
          </div>
        </div>
        <div className="panel">
          <h3>Safety</h3>
          <p>Rules every employee follows.</p>
          <div className="trow"><span>Ask before changing any data<small>Orders, PIs, vouchers, tasks, Tally</small></span><Switch on disabled label="Ask before changing any data" /></div>
          <div className="trow"><span>Who can talk to employees</span><span className="pill">Boss only</span></div>
          <div className="trow">
            <span>Daily AI spending limit<small>New work stops for the day when it is reached.{limitMsg ? ` ${limitMsg}` : ''}</small></span>
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span>$</span>
              <input style={{ width: 80 }} inputMode="decimal" aria-label="Daily limit in US dollars" placeholder={s ? String(limit || '') : '…'} value={limitDraft} onChange={(e) => setLimitDraft(e.target.value)} disabled={!s} />
              <button className="aio-btn" onClick={saveLimit} disabled={!s || limitDraft === '' || !(Number(limitDraft) > 0) || Number(limitDraft) === limit}>Save</button>
            </span>
          </div>
        </div>
        <div className="panel">
          <h3>AI cost</h3>
          <p>Paid to the AI provider, using the company's key.</p>
          {!s ? loadingNote : <>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            <span>{usd(used)} today</span><span style={{ color: 'var(--muted)' }}>of {usd(limit)} daily limit</span>
          </div>
          <div className="usage"><b style={{ width: `${limit ? Math.min(100, (used / limit) * 100) : 0}%` }} /></div>
          <div style={{ margin: '4px 0 10px', fontSize: 13, color: 'var(--muted)' }}>This month: {usd(s.usage.monthUSD)}</div>
          {s.usage.byAgent.length > 0 ? (
            <div className="tblw"><table>
              <thead><tr><th>Employee</th><th>Model</th><th className="num">This month</th></tr></thead>
              <tbody>{s.usage.byAgent.map((r) => <tr key={r.agentId || 'none'}><td>{r.name}</td><td>{modelShort(r.model)}</td><td className="num">{usd(r.usd)}</td></tr>)}</tbody>
            </table></div>
          ) : <p style={{ margin: 0, color: 'var(--muted)', fontSize: 13 }}>No AI usage yet.</p>}
          </>}
        </div>
      </div>
    </div>
  );
}
